import React, { useMemo, useRef, useState } from "react";
import withHydrationOnDemand from "react-hydration-on-demand";
import ReactPlayer from "react-player";
import { useImageSize } from "../../global/components/ImageWrapper/ImageWrapper.methods";
import { VideoLightCover } from "../../global/components/VideoLightCover/VideoLightCover";
import { usePrepareSettings } from "../../settingsPatterns/settingsPatterns.methods";
import StyledText from "../../shared/styleElements/StyledText/StyledText";
import { getCoverImgAltText, renderCoverImageOrIcon } from "./BoxVideo.methods";
import {
  BOX_VIDEO_CONTENT_PATTERN,
  BOX_VIDEO_PATTERN,
  StyledBoxVideo,
} from "./BoxVideo.styled";
import { IBoxVideoProps } from "./BoxVideo.types";
import ControlBar from "./elements/ControlBar";
import { isPortalSide } from "@ecp-boxes/helpers/helpers";
import {
  InstagramEmbed,
  PinterestEmbed,
  TikTokEmbed,
} from "react-social-media-embed";
import { Memo } from "@ecp-boxes/helpers/memoWrapper";

const BoxVideo: React.FC<IBoxVideoProps> = (props: IBoxVideoProps) => {
  const { settings } = usePrepareSettings({
    props: {
      ...props.displaySettings,
      ...props.contentSettings,
    },
    settingsPattern: BOX_VIDEO_PATTERN(),
  });

  const [containerRef, { width: imageWidth }] = useImageSize();
  const [progress, setProgress] = useState(0);
  const [isPlaying, setIsPlaying] = useState(settings.video.autoPlay);
  const [isMuted, setIsMuted] = useState(settings.video.muted);
  const [playbackRate, setPlaybackRate] = useState(settings.video.playbackRate);
  const [volume, setVolume] = useState(settings.video.volume / 100);

  const playerRef = useRef<ReactPlayer>(null);

  const { coverImage, videoURL } = usePrepareSettings({
    props: { ...props.content },
    settingsPattern: BOX_VIDEO_CONTENT_PATTERN(),
  }).settings;

  const handleProgress = (state: { played: number }) => {
    setProgress(state.played);
  };

  const playbackControls = {
    isPlaying,
    setIsPlaying,
    playbackRate,
    setPlaybackRate,
    isMuted,
    setIsMuted,
    setVolume,
    volume,
  };

  const renderVideoPlayer = useMemo(() => {
    switch (true) {
      case videoURL.length === 0:
        return null;
      case videoURL.includes("tiktok.com"):
        return (
          <TikTokEmbed
            url={videoURL}
            data-testid="social-media-video"
            width={"100%"}
            height={"100%"}
          />
        );
      case videoURL.includes("instagram.com"):
        return (
          <InstagramEmbed url={videoURL} data-testid="social-media-video" />
        );
      case videoURL.includes("pinterest.com"):
        return (
          <PinterestEmbed
            url={videoURL}
            width={"100%"}
            height={"100%"}
            data-testid="social-media-video"
          />
        );
      default:
        return (
          <>
            <ReactPlayer
              ref={playerRef}
              key={
                settings.video.showVideoControls
                  ? "video-controls"
                  : "no-video-controls"
              }
              data-testid="react-player"
              width="100%"
              height="100%"
              url={videoURL}
              playing={isPortalSide() ? isPlaying : false}
              loop={settings.video.loopVideo}
              light={
                !!coverImage && (
                  <VideoLightCover
                    coverImage={coverImage}
                    alt={getCoverImgAltText(
                      coverImage,
                      props.messages.box_video_content_seo_description
                    )}
                  />
                )
              }
              playbackRate={playbackRate}
              muted={isMuted}
              config={{
                youtube: {
                  playerVars: { showinfo: 0, rel: 0 },
                },
              }}
              className="video-container__player"
              onProgress={handleProgress}
              onPlay={() => setIsPlaying(true)}
              onPause={() => setIsPlaying(false)}
              playsinline
              onReady={() => {
                window.addEventListener("yt-page-data-updated", () => {
                  document
                    .querySelector(".ytp-pause-overlay-container")
                    ?.remove();
                });
                if (isPlaying) {
                  playerRef.current?.getInternalPlayer()["play"]();
                }
              }}
              volume={volume}
            />

            {settings.video.showVideoControls && (
              <ControlBar
                progress={progress}
                playerRef={playerRef}
                containerRef={containerRef}
                playbackControls={playbackControls}
              />
            )}
          </>
        );
    }
  }, [
    videoURL,
    settings.video,
    coverImage,
    props.messages.box_video_content_seo_description,
  ]);

  return (
    <StyledBoxVideo
      ref={containerRef}
      $settings={settings}
      url={videoURL}
      aria-label={props.messages.box_video_content_seo_description}
      data-testid="boxVideo"
      className="video-container video-custom-style"
    >
      {renderVideoPlayer}
      {videoURL.length === 0 &&
        renderCoverImageOrIcon(
          coverImage,
          imageWidth,
          props.messages.box_video_content_seo_description,
          settings.ratio,
          settings.orientation
        )}

      {props.messages.box_video_content_videoTitle.length > 0 &&
        settings.title.show && (
          <div className="video-container__title">
            <StyledText $settings={settings.title}>
              {props.messages.box_video_content_videoTitle}
            </StyledText>
          </div>
        )}
    </StyledBoxVideo>
  );
};

export default Memo(withHydrationOnDemand({ on: ["visible"] })(BoxVideo));
